import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, OnInit, PLATFORM_ID, QueryList, ViewChild, ViewChildren, inject } from '@angular/core';
import { SectionComponent } from '../../components/section/section.component';
import { CopyTextComponent, isCopyTextSection } from '../copy-text/copy-text.component';
import { MediumComponent, isMediumSection } from '../medium/medium.component';
import { DownloadListComponent, isDownloadListSection } from '../download-list/download-list.component';
import { AnySection, InnerSection, InnerSectionAttributes, NumColumns, Section, SectionAttributes } from '../../../app/types/section';
import { isPlatformBrowser } from '@angular/common';

import { EditorIdDirective } from '../../../app/directives/editor-id.directive';
import { TrackClickDirective } from '../../directives/track-click.directive';
import { TabGroupComponent, TabPanelComponent } from '@knorr-bremse-portals/ngx-components/components/tab-group';
import { PanelHeaderComponent } from '@knorr-bremse-portals/ngx-components/components/panel-header';
import { PanelContentComponent } from '@knorr-bremse-portals/ngx-components/components/panel-content';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { preventScroll } from '../../../app/providers/page.service';
import { ActivatedRoute, Router } from '@angular/router';
import { FsButtonComponent } from '../../components/fs-button/fs-button.component';
import { ColumnWidth } from '@knorr-bremse-portals/ngx-components';

export interface TabItemAttributes extends InnerSectionAttributes {
  sections: AnySection[];
  title: string;
  heading: '' | 'H2' | 'H3';
}

type TabItemEditorIds = [
  'title',
  'heading',
];

export type TabItemSection = InnerSection<TabItemAttributes, TabItemEditorIds, 'tab-item'>;

type TabsSection = Section<
  {
    numColumns: 4 | 8 | 12;
    headline: string;
    items: TabItemSection[];
  } & SectionAttributes,
  [
    'headline',
  ],
  'tabs'
>;

@Component({
    selector: 'app-tabs',
    styleUrls: ['./tabs.component.scss'],
    templateUrl: './tabs.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        EditorIdDirective,
        MediumComponent,
        CopyTextComponent,
        DownloadListComponent,
        TrackClickDirective,
        TabGroupComponent,
        TabPanelComponent,
        PanelHeaderComponent,
        PanelContentComponent,
        FsButtonComponent,
    ],
})
export class TabsComponent extends SectionComponent<TabsSection> implements OnInit, AfterViewInit {

  protected isDownloadList = isDownloadListSection;
  protected isCopyTextSection = isCopyTextSection;
  protected isMediumSection = isMediumSection;

  private platformBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  @ViewChild(TabGroupComponent, { static: true }) private tabGroup!: TabGroupComponent;
  @ViewChildren(TabPanelComponent) private tabPanels!: QueryList<TabPanelComponent>;

  public override ngOnInit(): void {
    super.ngOnInit();

    const columnAddition = ColumnWidth.Full / this.attributes.numColumns;
    const validColumnsWidths = Object.values(ColumnWidth);

    for (const tabItem of this.attributes.items) {
      for (const section of tabItem.attributes.sections) {
        const targetWidth = section.attributes.numColumns * columnAddition;
        if (validColumnsWidths.includes(targetWidth)) {
          section.attributes.numColumns = targetWidth as NumColumns;
        } else {
          section.attributes.numColumns = ColumnWidth.Full;
        }
      }
    }
  }

  public override ngAfterViewInit(): void {
    super.ngAfterViewInit();

    this.openInitialPanel();
    this.handleQueryParams();
  }

  private openInitialPanel(): void {
    if (!this.platformBrowser) {
      return;
    }

    const urlSelectedTitle = this.route.snapshot.queryParamMap.get(this.anchor);
    if (urlSelectedTitle === null) {
      return;
    }
    const foundIndex = this.attributes.items.findIndex((item) => item.attributes.title === urlSelectedTitle);
    if (foundIndex === -1) {
      return;
    }
    const panel = this.tabPanels.get(foundIndex);
    if (panel === undefined) {
      return;
    }
    this.tabGroup.open(panel);
  }

  private handleQueryParams(): void {
    const openItem$ = this.tabGroup.openPanelChange.pipe(
      map(openPanel => this.tabPanels.toArray().indexOf(openPanel)),
      map(openPanelIndex => this.attributes.items[openPanelIndex]),
      takeUntilDestroyed(this.destroyRef),
    );

    openItem$.subscribe((openItem) => {
      this.router.navigate([], preventScroll({
        relativeTo: this.route,
        queryParams: {
          [this.anchor]: openItem.attributes.title,
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      }, this.platformBrowser));
    });
  }

}
